import React, { useEffect, useRef, useState } from "react";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator_midnight.min.css";
import styles from "./AeternaInvicta.module.scss";

const CATALOG_TABS = [
  { kind: "equipment", label: "Equipment" },
  { kind: "ability", label: "Ability" },
  { kind: "aspect", label: "Aspect" },
];

const WIDE_FIELDS = new Set([
  "text",
  "effect",
  "augment",
  "activatedEffect",
  "passiveEffects",
  "resources",
  "abilitySlots",
  "cost",
  "onEquip",
  "onSlot",
  "invoke",
]);

function buildColumns(columnNames) {
  return (columnNames || [])
    .filter((field) => field !== "abilitySlots")
    .map((field) => {
      const wide = WIDE_FIELDS.has(field);
      return {
        title: field,
        field,
        editor: wide ? "textarea" : "input",
        headerFilter: "input",
        headerFilterPlaceholder: "Filter…",
        width: wide ? 260 : field === "id" || field === "name" ? 160 : 110,
        minWidth: wide ? 180 : 80,
        formatter: "textarea",
      };
    });
}

export default function AdminCardEditor({ send, catalog }) {
  const tableHostRef = useRef(null);
  const tableRef = useRef(null);
  const sendRef = useRef(send);
  const [kind, setKind] = useState("equipment");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  sendRef.current = send;

  const activeCatalog =
    catalog && catalog.kind === kind ? catalog : null;
  const columns = activeCatalog?.columns || [];
  const rows = activeCatalog?.rows || [];
  const file = activeCatalog?.file || "";
  const revision = activeCatalog?.revision || 0;

  useEffect(() => {
    if (!sendRef.current) return;
    setDirty(false);
    setSaving(false);
    sendRef.current({ type: "ai:adminGetCardCatalog", kind });
  }, [kind]);

  useEffect(() => {
    if (activeCatalog) {
      setDirty(false);
      setSaving(false);
    }
  }, [revision, kind, activeCatalog]);

  useEffect(() => {
    if (!saving) return undefined;
    const timer = window.setTimeout(() => setSaving(false), 8000);
    return () => window.clearTimeout(timer);
  }, [saving]);

  useEffect(() => {
    if (!tableHostRef.current || !activeCatalog) return undefined;

    if (tableRef.current) {
      tableRef.current.destroy();
      tableRef.current = null;
    }

    const table = new Tabulator(tableHostRef.current, {
      data: rows,
      columns: buildColumns(columns),
      layout: "fitDataStretch",
      height: "520px",
      reactiveData: false,
      movableColumns: true,
      selectableRows: true,
      placeholder: "No card rows",
      rowHeader: {
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        headerSort: false,
        width: 40,
      },
    });

    table.on("dataChanged", () => setDirty(true));
    table.on("cellEdited", () => setDirty(true));
    tableRef.current = table;

    return () => {
      table.destroy();
      if (tableRef.current === table) tableRef.current = null;
    };
  }, [revision, kind]);

  const handleAddRow = () => {
    const table = tableRef.current;
    if (!table) return;
    const blank = {};
    columns.forEach((column) => {
      blank[column] = "";
    });
    table.addRow(blank, true);
    setDirty(true);
  };

  const handleDeleteSelected = () => {
    const table = tableRef.current;
    if (!table) return;
    const selected = table.getSelectedRows();
    if (!selected.length) return;
    selected.forEach((row) => row.delete());
    setDirty(true);
  };

  const handleSave = () => {
    const table = tableRef.current;
    if (!table || !sendRef.current) return;
    setSaving(true);
    sendRef.current({
      type: "ai:adminSaveCardCatalog",
      kind,
      rows: table.getData(),
    });
  };

  const handleReload = () => {
    if (!sendRef.current) return;
    if (dirty && !window.confirm("Discard unsaved edits and reload from disk?")) {
      return;
    }
    sendRef.current({ type: "ai:adminReloadCardCatalog", kind });
  };

  return (
    <div className={styles.detailBlock}>
      <div className={styles.muted}>
        Edit committed CSV files on the server. Changes save to{" "}
        {file || "the matching *.csv"} and reload the live catalog for all
        connected clients. You can still edit the same files in the repo by hand.
      </div>
      <div className={styles.row}>
        {CATALOG_TABS.map((tab) => (
          <button
            key={tab.kind}
            type="button"
            className={`${styles.button} ${
              kind === tab.kind ? styles.buttonPrimary : ""
            }`}
            onClick={() => {
              if (
                dirty &&
                kind !== tab.kind &&
                !window.confirm("Discard unsaved edits and switch catalog?")
              ) {
                return;
              }
              setKind(tab.kind);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={styles.row}>
        <button type="button" className={styles.button} onClick={handleAddRow}>
          Add Row
        </button>
        <button
          type="button"
          className={styles.button}
          onClick={handleDeleteSelected}
        >
          Delete Selected
        </button>
        <button type="button" className={styles.button} onClick={handleReload}>
          Reload From Disk
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          disabled={saving || !dirty}
          onClick={handleSave}
        >
          {saving ? "Saving…" : dirty ? "Save CSV" : "Saved"}
        </button>
      </div>
      <div className={styles.muted}>
        {file
          ? `${file} · ${rows.length} rows${dirty ? " · unsaved changes" : ""}`
          : "Loading…"}
      </div>
      <div className={styles.adminCardTableHost} ref={tableHostRef} />
    </div>
  );
}
